import _ from 'lodash';
import dayjs from 'utils/dayjs';

import { ROOM_KEYS, REVENUE_KEYS, INVENTORY_KEYS, INVENTORY_SUB_KEYS } from 'utils/analytics/types';
import { sumObjectsProperties } from 'utils/objects';

import {
    CUMULATED_ROOM_REVENUE_FIGURES_KEYS,
    BUDGET_FIGURES_KEYS,
    getCumulatedRoomFigures,
    parseInteger,
} from '../../helpers/parsingHelper';
import { getDatesArrayForRange } from '../../helpers/dateHelper';
import { createInitialObject, groupBy, mergeArraysOfObjects, sumArrayProperties } from '../../helpers/utils';

/**
 * Parses a key's value from the first match found
 * @param  {object[]} groupedData - data for a period
 * @param  {string}   parsingKey  - key to parse the value
 * @return {number} the extracted value, or defaultValue if it was not found
 */
const parseKey = (groupedData, parsingKey, defaultValue = 0) => {
    if (!groupedData || groupedData.length === 0) return defaultValue;

    const parsed = groupedData[0][parsingKey];
    if (parsed === undefined || parsed === null) return defaultValue;
    return parseInteger(parsed);
};

/**
 * Parses the change in business since yesterday payload
 * @param  {object[]} data          - the data from the API
 * @param  {moment[]} datesRange    - the list of dates (days/months) the data should span
 * @param  {object}   options       - parsing options
 * @param  {string}   options.dateFormat - format of the dates to parse
 * @param  {string}   options.precision  - the precision with which to parse & compare dates (i.e. 'day' or 'month')
 * @return {object[]} an array of objects, with the date and rooms totals
 */
export const parseYesterdayChange = (data, datesRange, options) => {
    const { dateFormat, precision } = options;
    const groupedList = groupBy(data, 'strDate');
    return datesRange.map((date) => {
        const matchKey = Object.keys(groupedList).find((key) => dayjs(key, dateFormat).isSame(date, precision));
        return {
            date: date.clone(),
            ...getCumulatedRoomFigures(groupedList[matchKey], 'rooms'),
        };
    });
};

/**
 * Merges LY data into the current data, and complete missing rows
 * @param  {Object[]} data       - the parsed current data
 * @param  {Object[]} lySpitData - the parsed LYSpitData
 * @param  {Object}   options    - options for comparing dates
 * @param  {string}   options.precision - precision of date comparison, i.e. day or month
 * @param  {callback} options.getLySpitDate - function to retrieve the LYSpit date given a date
 * @return {Object[]} the Ly data object copied in the current data's objects
 *                    under the "lySpit" key
 */
export const mergeLastYearData = (data, lySpitData, options = {}) =>
    mergeArraysOfObjects(data, lySpitData, {
        mergeKey: 'lySpit',
        objectMatcher: (row, lySpitRow) => lySpitRow.date.isSame(options.getLySpitDate(row.date), options.precision),
        noMatchValue: createInitialObject(CUMULATED_ROOM_REVENUE_FIGURES_KEYS, 0),
    });

/**
 * Merge yesterday's data
 * @param  {Object[]} data          - the parsed current data
 * @param  {Object[]} changeData - the parsed yesterday's data
 * @param  {Object}   options       - options for comparing dates
 * @param  {string}   options.precision - precision of date comparison, i.e. day or month
 * @return {Object[]} yesterday's data copied in the current data's objects
 *                    under the "yesterday" key
 */
export const mergeChangeData = (data, changeData, options = {}) =>
    mergeArraysOfObjects(data, changeData, {
        mergeKey: 'change',
        objectMatcher: (row, changeRow) => dayjs(changeRow.stayDate).isSame(dayjs(row.date), options.precision),
    });

/**
 * Calculate the cumulated totals
 * @param  {Object[]} data - array of data objects to sum
 * @return {Object} the object with totals value for each property
 */
export const calculateTotals = (data) => {
    const totals = sumArrayProperties(data, CUMULATED_ROOM_REVENUE_FIGURES_KEYS);
    const lyTotals = sumArrayProperties(
        data.map((row) => row.lySpit),
        CUMULATED_ROOM_REVENUE_FIGURES_KEYS,
    );
    const budgetTotals = sumArrayProperties(
        data.map((row) => row.budget),
        BUDGET_FIGURES_KEYS,
        null,
    );
    const inventoryTotals = sumArrayProperties(
        data.map((row) => row.inventory),
        INVENTORY_SUB_KEYS,
    );
    // Use guaranteed rooms/revenue when forecast is missing, if there is any forecast for the whole period
    let forecastedRoomsTotals = 0;
    let forecastedRevenueTotals = 0;
    let hasRoomForecast = false;
    let hasRevenueForecast = false;
    data.forEach((row) => {
        if (_.get(row, 'forecast.rooms')) {
            hasRoomForecast = true;
            forecastedRoomsTotals += _.get(row, 'forecast.rooms');
        } else {
            forecastedRoomsTotals += row.guaranteedRooms;
        }
        if (_.get(row, 'forecast.revenue')) {
            hasRevenueForecast = true;
            forecastedRevenueTotals += _.get(row, 'forecast.revenue');
        } else {
            forecastedRevenueTotals += row.guaranteedRevenue;
        }
    });

    return {
        ...totals,
        lySpit: lyTotals,
        budget: budgetTotals,
        forecast: {
            rooms: hasRoomForecast ? forecastedRoomsTotals : null,
            revenue: hasRevenueForecast ? forecastedRevenueTotals : null,
        },
        inventory: inventoryTotals,
    };
};

/**
 * This function merges stats and budget from new API (new format) and re-formats it
 * into the same format as `parseData` returns
 * @param  {Object[]} stats - 1 object per month, with rooms, revenue & inventory
 * @param  {Object[]} budgetData - 1 object per month, with budget
 * @param  {Object[]} forecastData
 * @return {Object[]} 1 object per month, containing stats, budget and inventory
 */
export const mergeStats = (stats, budgetData = [], forecastData = []) =>
    stats.map((statRow) => {
        const { stayDate } = statRow;
        const budget = budgetData.find((row) => row.stayDate === stayDate);
        const forecast = forecastData.find((row) => row.stayDate === stayDate);

        return {
            ...statRow,
            budget,
            forecast,
        };
    });

/**
 * Formats data coming from new API format (with budget merged) into the same format as `parseData` returns
 * @param  {Object[]} data
 * @param  {Object} options
 * @param  {String} options.precision
 * @return {Object[]}
 */
export const formatIntoCalendarViewFormat = (data, options) =>
    data.map((row) => {
        const { stayDate, rooms, revenue, budget, ...rest } = row;
        return {
            date: dayjs(stayDate).startOf(options.precision),
            totalRooms: rooms.guaranteed + rooms.nonGuaranteed + rooms.tentative,
            guaranteedRooms: rooms.guaranteed,
            nonGuaranteedRooms: rooms.nonGuaranteed,
            tentativeRooms: rooms.tentative,
            cancelledRooms: rooms.cancelled,
            totalRevenue:
                revenue.accommodation.guaranteed +
                revenue.accommodation.nonGuaranteed +
                revenue.accommodation.tentative,
            guaranteedRevenue: revenue.accommodation.guaranteed,
            nonGuaranteedRevenue: revenue.accommodation.nonGuaranteed,
            tentativeRevenue: revenue.accommodation.tentative,
            cancelledRevenue: revenue.accommodation.cancelled,
            budget: budget && {
                roomsBudget: budget.rooms,
                accommodationRevenueBudget: budget.revenue.accommodation,
                fbRevenueBudget: budget.revenue.foodAndBeverage,
                ancillaryRevenueBudget: budget.revenue.ancillary,
                otherRevenueBudget: budget.revenue.other,
                adrBudget: null, // unused
                revParBudget: null, // unused
            },
            ...rest,
        };
    });

/**
 * Groups the forecast by months, using guaranteed values if the forecast is in the future and missing
 * @param {Object[]} forecastData
 * @param {Object[]} roomsRevenue
 * @return {Object[]}
 */
export const groupByMonthAndAddMissingForecast = (roomsRevenue, forecastData) => {
    const data = [];
    const forecast = [];

    const statsByMonth = _.groupBy(roomsRevenue, (row) => dayjs(row.stayDate).format('YYYY-MM'));

    Object.entries(statsByMonth).forEach(([month, rows]) => {
        const startOfMonth = dayjs(month).startOf('month');
        const endOfMonth = dayjs(month).endOf('month');

        data.push({
            stayDate: month,
            ...rows.reduce(
                (accu, row) => sumObjectsProperties(accu, row, [...ROOM_KEYS, ...REVENUE_KEYS, ...INVENTORY_KEYS]),
                {},
            ),
        });

        // If the whole month is in the past, ignore forecast
        if (endOfMonth.isBefore(dayjs(), 'day')) {
            forecast.push({
                stayDate: month,
                rooms: null,
                revenue: null,
            });
        } else {
            const forecastTotalForMonth = {
                rooms: 0,
                revenue: 0,
                unconstrained: {
                    rooms: 0,
                    revenue: 0,
                },
            };
            getDatesArrayForRange(startOfMonth, endOfMonth).forEach((date) => {
                const forecastForDate = forecastData.find((row) => row.stayDate === date.format('YYYY-MM-DD'));
                const statsForDate = rows.find((row) => row.stayDate === date.format('YYYY-MM-DD'));
                const guaranteedRooms = _.get(statsForDate, 'rooms.guaranteed', 0);
                const guaranteedRevenue = _.get(statsForDate, 'revenue.accommodation.guaranteed', 0);

                forecastTotalForMonth.rooms += _.get(forecastForDate, 'rooms', guaranteedRooms);
                forecastTotalForMonth.unconstrained.rooms += _.get(
                    forecastForDate,
                    'unconstrained.rooms',
                    guaranteedRooms,
                );
                forecastTotalForMonth.revenue += _.get(forecastForDate, 'revenue', guaranteedRevenue);
                forecastTotalForMonth.unconstrained.revenue += _.get(
                    forecastForDate,
                    'unconstrained.revenue',
                    guaranteedRevenue,
                );
            });
            forecast.push({
                stayDate: month,
                ...forecastTotalForMonth,
            });
        }
    });

    return { data, forecast };
};

export default {
    parseKey,
    mergeLastYearData,
    mergeChangeData,
    calculateTotals,
    groupByMonthAndAddMissingForecast,
};
